import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PlusCircle, Trash, PencilSimple, CheckCircle, Clock, XCircle, PauseCircle, PlayCircle, Coins } from "@phosphor-icons/react";

export default function Websites() {
  const [sites, setSites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ url: "", title: "", category: "general" });
  const [editing, setEditing] = useState(null);
  const [assignCredits, setAssignCredits] = useState({});
  const { user, refresh } = useAuth();

  const load = async () => {
    const { data } = await api.get("/websites");
    setSites(data);
    refresh?.();
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/websites/${editing.id}`, form);
        toast.success("Website updated");
      } else {
        await api.post("/websites", form);
        toast.success("Website submitted — awaiting approval");
      }
      setForm({ url: "", title: "", category: "general" });
      setShowForm(false); setEditing(null);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const toggleActive = async (s) => {
    await api.patch(`/websites/${s.id}`, { active: !s.active });
    load();
  };

  const del = async (s) => {
    const unused = Math.max(0, (s.credits_assigned || 0) - (s.views_earned || 0));
    const warn = unused > 0
      ? `Delete "${s.title}"? ${unused} unused credit${unused === 1 ? "" : "s"} will be refunded to your balance.`
      : `Delete "${s.title}"?`;
    if (!confirm(warn)) return;
    try {
      const { data } = await api.delete(`/websites/${s.id}`);
      const refunded = Number(data?.refunded_credits || 0);
      if (refunded > 0) toast.success(`Deleted — ${refunded} credit${refunded === 1 ? "" : "s"} refunded`);
      else toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({ url: s.url, title: s.title, category: s.category });
    setShowForm(true);
  };

  const assign = async (s) => {
    const amt = parseInt(assignCredits[s.id] || "0", 10);
    if (!amt) return;
    try {
      await api.patch(`/websites/${s.id}`, { credits_assigned: (s.credits_assigned || 0) + amt });
      toast.success(`Assigned ${amt} credits`);
      setAssignCredits({ ...assignCredits, [s.id]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusBadge = (s) => {
    if (s.status === "approved") return <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"><CheckCircle size={12} weight="fill" /> Approved</span>;
    if (s.status === "rejected") return <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"><XCircle size={12} weight="fill" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300"><Clock size={12} weight="fill" /> Pending</span>;
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Traffic Exchange</div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>My Websites</h1>
        </div>
        <button data-testid="add-website-btn" onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ url: "", title: "", category: "general" }); }} className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-bold px-4 py-2 transition-colors duration-200"><PlusCircle size={18} weight="fill" /> Add website</button>
      </div>

      {/* Total credits available — decreases when assigned, refunded on delete */}
      <div
        data-testid="my-sites-credits-card"
        className="mb-5 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 text-yellow-950 px-5 py-4 flex items-center justify-between gap-4 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-yellow-950/10 flex items-center justify-center">
            <Coins size={22} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Available credits</div>
            <div
              className="text-3xl font-black tabular-nums"
              data-testid="my-sites-credits-value"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              {Number(user?.credits ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </div>
          </div>
        </div>
        <div className="text-xs font-semibold text-right max-w-[200px] leading-snug hidden sm:block">
          Credits go down when you assign them below. Delete a site and any unused credits are refunded automatically.
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5 grid md:grid-cols-4 gap-3">
          <input data-testid="website-url-input" required placeholder="https://your-site.com" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="md:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
          <input data-testid="website-title-input" required placeholder="Title (max 40 chars)" maxLength={40} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 40) })} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
          <select data-testid="website-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100">
            {["general", "tech", "business", "health", "info", "shopping", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="md:col-span-4 flex gap-2">
            <button data-testid="website-submit-btn" className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-5 py-2 transition-colors duration-200">{editing ? "Update" : "Submit for approval"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-5 py-2">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {sites.length === 0 && <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500">No websites yet. Add your first site to start driving traffic.</div>}
        {sites.map((s) => (
          <div key={s.id} data-testid={`website-row-${s.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <a href={s.url} target="_blank" rel="noreferrer" className="font-bold text-slate-900 dark:text-white hover:text-blue-700 dark:hover:text-blue-400">{s.title}</a>
                {statusBadge(s)}
                <span className="text-xs uppercase tracking-widest text-slate-500">{s.category}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{s.url}</div>
              {s.rejection_reason && <div className="text-xs text-red-600 mt-1">Rejection: {s.rejection_reason}</div>}
            </div>
            <div className="text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Credits Assigned</div>
              <div className="text-lg font-black text-slate-900 dark:text-white">{s.credits_assigned}</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Views</div>
              <div className="text-lg font-black text-slate-900 dark:text-white">{s.views_earned}</div>
            </div>
            <div className="flex items-center gap-1">
              <input data-testid={`assign-input-${s.id}`} type="number" min="1" placeholder="+credits" value={assignCredits[s.id] || ""} onChange={(e) => setAssignCredits({ ...assignCredits, [s.id]: e.target.value })} className="w-24 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 text-sm text-slate-900 dark:text-slate-100" />
              <button data-testid={`assign-btn-${s.id}`} onClick={() => assign(s)} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold px-3 py-1.5 transition-colors duration-200">Assign</button>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid={`toggle-active-${s.id}`} onClick={() => toggleActive(s)} className="h-9 w-9 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 inline-flex items-center justify-center transition-colors duration-200">
                {s.active ? <PauseCircle size={20} weight="fill" /> : <PlayCircle size={20} weight="fill" />}
              </button>
              <button data-testid={`edit-btn-${s.id}`} onClick={() => startEdit(s)} className="h-9 w-9 rounded-full bg-slate-100 dark:bg-slate-800 text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-500/20 inline-flex items-center justify-center transition-colors duration-200"><PencilSimple size={18} weight="bold" /></button>
              <button data-testid={`delete-btn-${s.id}`} onClick={() => del(s)} className="h-9 w-9 rounded-full bg-slate-100 dark:bg-slate-800 text-red-600 hover:bg-red-100 dark:hover:bg-red-500/20 inline-flex items-center justify-center transition-colors duration-200"><Trash size={18} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
